import React from 'react';
import { ApplogEntryDto } from 'src/api/FiretrolApiClient';
import { Box } from 'src/design/atoms/Box';
import { Code } from 'src/design/atoms/Code';
import { Group } from 'src/design/atoms/Group';
import { ScrollArea } from 'src/design/atoms/ScrollArea';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Modal } from 'src/design/molecules/Modal';
import { ApplogLevelBadge } from 'src/routes/Settings/Applogs/components/ApplogLevelBadge';
import { formatApplogTimestamp } from 'src/routes/Settings/Applogs/applogFilters';
import { formatJsonString } from 'src/utils';

export interface IApplogDetailModalProps {
  entry: ApplogEntryDto | null;
  onClose: () => void;
}

const SectionLabel: React.FC<{ children: string }> = ({ children }) => (
  <Text size="sm" fw={700}>
    {children}
  </Text>
);

export const ApplogDetailModal: React.FC<IApplogDetailModalProps> = ({
  entry,
  onClose,
}) => {
  const properties = formatJsonString(entry?.properties);

  return (
    <Modal
      opened={entry !== null}
      onClose={onClose}
      title="Log Entry"
      size="xl"
      closeButtonProps={{ 'aria-label': 'Close log entry' }}
    >
      {entry && (
        <Stack gap="md">
          <Group gap="sm">
            <ApplogLevelBadge level={entry.level} />
            <Text size="sm">{formatApplogTimestamp(entry.timestamp)}</Text>
          </Group>

          <Stack gap={4}>
            <SectionLabel>Message</SectionLabel>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {entry.message}
            </Text>
          </Stack>

          {entry.exceptionType && (
            <Stack gap={4}>
              <SectionLabel>Exception</SectionLabel>
              <Text size="sm" ff="monospace">
                {entry.exceptionType}
              </Text>
            </Stack>
          )}

          {entry.exceptionDetails && (
            <Stack gap={4}>
              <SectionLabel>Exception Details</SectionLabel>
              <ScrollArea.Autosize mah={280}>
                <Box style={{ overflowX: 'auto' }}>
                  <Code block>{entry.exceptionDetails}</Code>
                </Box>
              </ScrollArea.Autosize>
            </Stack>
          )}

          <Stack gap={4}>
            <SectionLabel>Operation ID</SectionLabel>
            <Text size="sm" ff="monospace">
              {entry.operationId || '—'}
            </Text>
          </Stack>

          {properties && (
            <Stack gap={4}>
              <SectionLabel>Properties</SectionLabel>
              <ScrollArea.Autosize mah={280}>
                <Box style={{ overflowX: 'auto' }}>
                  <Code block>{properties}</Code>
                </Box>
              </ScrollArea.Autosize>
            </Stack>
          )}
        </Stack>
      )}
    </Modal>
  );
};
